'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import NewsletterSection from '@/components/NewsletterSection';

interface Project {
  id: string;
  category: string;
  title: string;
  status: string;
  statusVariant: 'active' | 'development' | 'planning';
  location: string;
  description: string;
  href: string;
}

const projects: Project[] = [
{
  id: 'tilenga',
  category: 'Upstream',
  title: 'Tilenga Project',
  status: 'In Development',
  statusVariant: 'development',
  location: 'Buliisa & Nwoya Districts',
  description: '~190,000 barrels/day peak production from Lake Albert basin oil fields with 400+ wells across 6 fields and a Central Processing Facility at Buliisa.',
  href: '/projects/tilenga'
},
{
  id: 'kingfisher',
  category: 'Upstream',
  title: 'Kingfisher Development',
  status: 'In Development',
  statusVariant: 'development',
  location: 'Kikuube District',
  description: '~40,000 barrels/day peak production from Buhuka Flats on Lake Albert shores with 132 production wells and a Central Processing Facility at Kasenyi.',
  href: '/projects/kingfisher'
},
{
  id: 'eacop',
  category: 'Midstream',
  title: 'East African Crude Oil Pipeline',
  status: 'Under Construction',
  statusVariant: 'active',
  location: 'Hoima, Uganda → Tanga, Tanzania',
  description: "1,443 km heated and buried pipeline — the world's longest electrically heated crude oil pipeline, designed to transport 216,000 barrels per day.",
  href: '/projects/eacop'
},
{
  id: 'industrial-park',
  category: 'Midstream',
  title: 'Kabaale Industrial Park',
  status: 'Phase 1 Active',
  statusVariant: 'active',
  location: 'Hoima District',
  description: '29.57 sq km industrial hub hosting the refinery site, pipeline terminal, international airport, and Special Economic Zone infrastructure.',
  href: '/projects/industrial-park'
},
{
  id: 'refinery',
  category: 'Downstream',
  title: 'Uganda Oil Refinery',
  status: 'Planning Phase',
  statusVariant: 'planning',
  location: 'Kabaale, Hoima District',
  description: "60,000 barrels/day processing capacity for domestic consumption and regional export, anchoring Uganda's national energy security.",
  href: '/projects/refinery'
},
{
  id: 'bulk-trading',
  category: 'Downstream',
  title: 'Bulk Petroleum Trading',
  status: 'Operational',
  statusVariant: 'active',
  location: 'Jinja & Kampala, Uganda',
  description: "UNOC's commercial arm managing bulk fuel procurement, strategic storage at Jinja Terminal, and distribution across Uganda and the region.",
  href: '/projects/bulk-trading'
}];


function ProjectCard({ project, index }: {project: Project;index: number;}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setVisible(true);},
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const statusClasses = {
    active: 'bg-[#CC0000] text-white',
    development: 'bg-[#0F0F0F] text-[#CC0000] border border-[#CC0000]/30',
    planning: 'bg-gray-200 text-gray-700'
  };

  return (
    <div
      ref={ref}
      className="h-full"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(20px)',
        transition: `opacity 0.5s ease ${index * 0.1}s, transform 0.5s ease ${index * 0.1}s`
      }}>
      
      <Link
        href={project.href}
        className="block bg-white border-r border-b border-gray-200 p-8 hover:bg-[#0F0F0F] transition-colors duration-300 group h-full flex flex-col">
        
        <div className="flex items-start justify-between mb-6 gap-4">
          <div>
            <span className="text-[#CC0000] text-[10px] font-bold uppercase tracking-[0.15em] font-heading block mb-2">
              {project.category}
            </span>
            <h3 className="text-2xl font-extrabold text-[#0F0F0F] font-heading group-hover:text-white transition-colors leading-snug">
              {project.title}
            </h3>
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.15em] whitespace-nowrap flex-shrink-0 ${statusClasses[project.statusVariant]}`}>
            {project.status}
          </span>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-gray-400 mb-5 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000] flex-shrink-0" aria-hidden="true">
            <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span>{project.location}</span>
        </div>

        <p className="text-gray-600 group-hover:text-gray-300 leading-relaxed mb-8 flex-1 transition-colors text-[15px]">
          {project.description}
        </p>

        <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100 group-hover:border-white/10 transition-colors">
          <span className="text-xs font-bold text-[#0F0F0F] group-hover:text-[#CC0000] uppercase tracking-[0.15em] transition-colors font-heading">
            View project
          </span>
          <span className="w-8 h-8 bg-[#CC0000] flex items-center justify-center rounded-sm group-hover:rotate-45 transition-transform duration-300">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white" aria-hidden="true">
              <path d="M7 7h10v10" />
              <path d="M7 17 17 7" />
            </svg>
          </span>
        </div>
      </Link>
    </div>);

}

export default function ProjectsPage() {
  return (
    <div className="w-full min-h-screen bg-white flex flex-col">
      <Navbar />

      <main id="main-content" className="flex-1">
        {/* Hero Section */}
        <section className="bg-[#0F0F0F] pt-40 pb-20 relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(204, 0, 0) 1px, transparent 0px)',
              backgroundSize: '40px 40px'
            }} />
          
          <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.03] pointer-events-none">
            <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-[#CC0000] blur-[120px] rounded-full" />
          </div>

          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 relative">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs font-mono tracking-[0.15em] uppercase text-gray-500 mb-8" aria-label="Breadcrumb">
              <Link href="/" className="hover:text-[#CC0000] transition-colors">Home</Link>
              <span className="text-[#CC0000]">/</span>
              <span className="text-[#CC0000]">Our Business</span>
            </nav>

            {/* Label */}
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 01 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Our Projects</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white mb-6 font-heading leading-[1.05] tracking-tight max-w-4xl">
              Our <span className="text-[#CC0000]">Business</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl leading-relaxed">
              Explore UNOC&apos;s portfolio of major projects across upstream, midstream, and downstream operations — driving Uganda&apos;s emergence as an oil-producing nation.
            </p>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#CC0000] to-transparent opacity-50" />
        </section>

        {/* Featured Banner Image */}
        <section className="bg-white pt-16">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="relative overflow-hidden bg-[#0F0F0F]">
              <img
                src="https://www.unoc.co.ug/wp-content/uploads/2025/12/WhatsApp-Image-2025-12-20-at-2.15.50-PM.jpeg"
                alt="UNOC oil infrastructure development across Uganda petroleum sector operations"
                className="w-full h-[320px] sm:h-[420px] lg:h-[500px] object-cover opacity-80" />
              
              <div className="absolute inset-0 bg-gradient-to-r from-[#0F0F0F]/85 via-[#0F0F0F]/30 to-transparent pointer-events-none" />
              <div className="absolute top-6 left-6 w-8 h-8 border-l-2 border-t-2 border-[#CC0000]" />
              <div className="absolute bottom-6 right-6 w-8 h-8 border-r-2 border-b-2 border-[#CC0000]" />
              <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">/</span>
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Flagship Portfolio</span>
                </div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold font-heading max-w-3xl leading-[1.1] tracking-tight">
                  Building the infrastructure that powers a nation
                </h2>
              </div>
            </div>
          </div>
        </section>

        {/* Business Segments Overview */}
        <section className="py-16 bg-white border-t border-gray-100">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid md:grid-cols-3 gap-0 border border-gray-200">
              {[
              {
                label: 'Upstream',
                icon: '⛏',
                desc: 'Exploration and production of crude oil from the Lake Albert basin — Tilenga and Kingfisher projects.',
                href: '/projects/tilenga'
              },
              {
                label: 'Midstream',
                icon: '🔧',
                desc: 'Transportation and processing infrastructure — EACOP pipeline and Kabaale Industrial Park.',
                href: '/projects/eacop'
              },
              {
                label: 'Downstream',
                icon: '⚡',
                desc: 'Refining, bulk trading, and distribution of petroleum products for domestic and regional markets.',
                href: '/projects/refinery'
              }].
              map((seg, i) =>
              <Link
                key={seg.label}
                href={seg.href}
                className={`p-8 group hover:bg-[#CC0000] transition-colors duration-300 ${i < 2 ? 'border-r border-gray-200' : ''}`}>
                
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#CC0000] group-hover:bg-white/20 flex items-center justify-center rounded-sm transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                      </svg>
                    </div>
                    <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#CC0000] group-hover:text-white font-heading transition-colors">{seg.label}</span>
                  </div>
                  <p className="text-sm text-gray-600 group-hover:text-white/80 leading-relaxed transition-colors">{seg.desc}</p>
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-24 bg-[#F8F9FA]">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="flex items-center gap-3 mb-12">
              <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 02 ]</span>
              <div className="h-[1px] w-12 bg-[#CC0000]" />
              <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">All Projects</span>
            </div>
            <div className="grid md:grid-cols-2 gap-0 border-t border-l border-gray-200">
              {projects.map((project, index) =>
              <ProjectCard key={project.id} project={project} index={index} />
              )}
            </div>
          </div>
        </section>

        {/* Strategic Storage & Distribution */}
        <section className="py-24 bg-white border-t border-gray-200">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="bg-[#F8F9FA] p-8 border border-gray-200 order-2 lg:order-1">
                <img
                  src="https://www.unoc.co.ug/wp-content/uploads/2021/04/ecop.jpg"
                  alt="UNOC fuel storage and pipeline infrastructure in Uganda"
                  className="w-full h-auto" />
                
              </div>
              <div className="order-1 lg:order-2">
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 03 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Operations</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0F0F0F] mb-6 font-heading leading-[1.05] tracking-tight">
                  Strategic Storage &amp; <span className="text-[#CC0000]">Distribution</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5 text-lg">
                  UNOC operates strategic petroleum storage facilities — including the Jinja Storage Terminal — that strengthen Uganda&apos;s energy security and ensure reliable supply across the region.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  These assets sit at the heart of UNOC&apos;s downstream commercial activities, supporting bulk fuel trading, distribution partnerships, and emergency reserve capacity for the country.
                </p>
                <div className="mt-8">
                  <Link
                    href="/projects/bulk-trading"
                    className="inline-flex items-center gap-2 bg-[#CC0000] text-white px-6 py-3 rounded-sm text-sm font-bold uppercase tracking-[0.1em] hover:bg-[#AA0000] transition-colors font-heading">
                    
                    Learn More
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 7h10v10" /><path d="M7 17 17 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Second Licensing Round Acreage */}
        <section className="py-24 bg-[#F8F9FA] border-t border-gray-200">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <span className="font-mono text-xs text-[#CC0000] tracking-[0.2em]">[ 04 ]</span>
                  <div className="h-[1px] w-12 bg-[#CC0000]" />
                  <span className="text-[#CC0000] text-xs font-bold uppercase tracking-[0.2em] font-heading">Exploration</span>
                </div>
                <h2 className="text-4xl sm:text-5xl font-extrabold text-[#0F0F0F] mb-6 font-heading leading-[1.05] tracking-tight">
                  Second Licensing <span className="text-[#CC0000]">Round Acreage</span>
                </h2>
                <p className="text-gray-600 leading-relaxed mb-5 text-lg">
                  Beyond the producing assets, UNOC is actively expanding Uganda&apos;s exploration footprint. The announced blocks from the country&apos;s Second Licensing Round span the Albertine Graben — Avivi, Omuka, Kasuruban, Turaco and Ngaji.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg">
                  UNOC participates as a national partner in these blocks, ensuring strategic state interest in future exploration and development opportunities.
                </p>
              </div>
              <div className="bg-white p-8 border border-gray-200">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1187e7cbf-1778365791245.png"
                  alt="Map of exploration blocks in Uganda Albertine Graben petroleum licensing"
                  className="w-full h-auto" />
                
              </div>
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="bg-[#0F0F0F] py-16 border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 divide-x divide-white/10">
              {[
              { value: '6.5B', label: 'Barrels of recoverable oil' },
              { value: '1,443km', label: 'EACOP pipeline length' },
              { value: '60K', label: 'Barrels/day refinery capacity' },
              { value: '10+', label: 'Years of operations' }].
              map((stat, i) =>
              <div key={i} className="px-8 py-6 text-center first:pl-0 last:pr-0">
                  <div className="text-4xl sm:text-5xl font-extrabold text-white mb-2 font-heading tracking-tight">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400 uppercase tracking-[0.15em]">{stat.label}</div>
                  <div className="mt-3 h-[2px] w-8 bg-[#CC0000] mx-auto" />
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <NewsletterSection />
      <Footer />
    </div>);

}